// Sprint · Slice 7.1 · Ad Accounts admin page.
//
// Lets an owner/editor bind Meta ad accounts (or unbind), see the
// current daily spend + $25/day global cap, and inspect the runtime
// feature flag that decides whether the Meta Marketing API is called
// for real (live) or short-circuited to a sandbox envelope (test_mode).
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function StatusPill({ status }) {
  const tone = status === "ACTIVE" ? "success"
    : status === "REVOKED" ? "muted" : "warn";
  return <Pill tone={tone} testId={`ad-account-status-${status}`}>{status}</Pill>;
}

function BindAdAccountForm({ onCancel, onCreated }) {
  const [provider] = useState("meta_ads");
  const [externalId, setExternalId] = useState("act_");
  const [displayName, setDisplayName] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [testModeDefault, setTestModeDefault] = useState(true);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api.post("/ads/ad-accounts/bind", {
        provider,
        external_ad_account_id: externalId.trim(),
        display_name: displayName.trim(),
        business_id: businessId.trim() || undefined,
        currency,
        test_mode_default: testModeDefault,
        notes: notes.trim(),
      });
      onCreated(r.data);
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} data-testid="bind-ad-account-form"
          style={{ display: "grid", gap: 14 }}>
      <ErrorBanner error={error} testId="bind-ad-account-error" />

      <div className="bo-field">
        <label className="bo-label">Provider</label>
        <input className="bo-input" value="Meta Ads (Marketing API)" disabled
               data-testid="bind-ad-account-provider" />
        <div className="bo-hint">
          LinkedIn, TikTok, YouTube, and Google Ads scaffolds arrive in
          the next slice. Meta ships first.
        </div>
      </div>

      <div className="bo-field">
        <label className="bo-label">Meta Ad Account ID</label>
        <input
          className="bo-input"
          data-testid="bind-ad-account-external-id"
          placeholder="act_1234567890"
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          required
        />
        <div className="bo-hint">
          Find in Meta Ads Manager → Ad account settings → Account ID.
          Must be prefixed with <code>act_</code> and 6-20 digits.
        </div>
      </div>

      <div className="bo-field">
        <label className="bo-label">Display name</label>
        <input
          className="bo-input"
          data-testid="bind-ad-account-display-name"
          placeholder="e.g. Precipice Health · US Growth"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
      </div>

      <div className="bo-field">
        <label className="bo-label">Business Manager ID (optional)</label>
        <input
          className="bo-input"
          data-testid="bind-ad-account-business-id"
          placeholder="e.g. 100081234567890"
          value={businessId}
          onChange={(e) => setBusinessId(e.target.value)}
        />
      </div>

      <div className="bo-field">
        <label className="bo-label">Currency</label>
        <select className="bo-input" value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                data-testid="bind-ad-account-currency">
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
          <option value="CAD">CAD</option>
        </select>
      </div>

      <label className="bo-check" style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}
             data-testid="bind-ad-account-test-mode-toggle">
        <input type="checkbox" checked={testModeDefault}
               onChange={(e) => setTestModeDefault(e.target.checked)} />
        <span>Default publishes to <strong>sandbox / test_mode</strong> until live is enabled</span>
      </label>

      <div className="bo-field">
        <label className="bo-label">Notes (optional)</label>
        <textarea className="bo-input" rows={2}
                  data-testid="bind-ad-account-notes"
                  value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="bo-btn bo-btn-ghost"
                data-testid="bind-ad-account-cancel"
                onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="bo-btn bo-btn-primary"
                data-testid="bind-ad-account-submit"
                disabled={busy}>{busy ? "Binding…" : "Bind ad account"}</button>
      </div>
    </form>
  );
}

function AdAccountRow({ row, onUnbind, unbinding }) {
  return (
    <div className="bo-card" data-testid={`ad-account-row-${row.id}`}
         style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "var(--bo-serif)", fontSize: 18, color: "var(--bo-ink)" }}
               data-testid={`ad-account-name-${row.id}`}>
            {row.display_name}
          </div>
          <div style={{ fontSize: 12, color: "var(--bo-muted)", fontFamily: "monospace" }}
               data-testid={`ad-account-external-id-${row.id}`}>
            {row.external_ad_account_id}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Pill tone="muted" testId={`ad-account-provider-${row.id}`}>Meta Ads</Pill>
          {row.test_mode_default && (
            <Pill tone="warn" testId={`ad-account-testmode-${row.id}`}>Sandbox default</Pill>
          )}
          <StatusPill status={row.status} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 20, fontSize: 13, color: "var(--bo-muted)" }}>
        <div><strong>Currency:</strong> {row.currency}</div>
        <div><strong>Health:</strong> {row.health}</div>
        <div><strong>Business ID:</strong> {row.business_id || "—"}</div>
        <div><strong>Bound:</strong> {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}</div>
      </div>
      {row.status === "ACTIVE" && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="bo-btn bo-btn-ghost"
                  data-testid={`ad-account-unbind-${row.id}`}
                  disabled={unbinding} onClick={() => onUnbind(row.id)}>
            {unbinding ? "Unbinding…" : "Unbind"}
          </button>
        </div>
      )}
    </div>
  );
}

function SpendStatusPanel({ status, flags }) {
  if (!status) return <LoadingCard label="Loading spend status…" />;
  const g = status.global_daily;
  const pct = Math.min(100, Math.round((g.spent_today_usd / g.cap_usd) * 100));
  const dangerZone = pct >= 80;
  return (
    <div className="bo-card" data-testid="ad-spend-status">
      <div className="bo-eyebrow">TODAY · {status.day_utc} UTC</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "var(--bo-serif)", fontSize: 22, color: "var(--bo-ink)" }}
               data-testid="ad-spend-status-headline">
            ${g.spent_today_usd.toFixed(2)}
            <span style={{ color: "var(--bo-muted)", fontSize: 14, marginLeft: 8 }}>
              of ${g.cap_usd.toFixed(2)} daily cap
            </span>
          </div>
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 4 }}>
            Sandbox spend today (not counted against cap):
            <strong style={{ color: "var(--bo-ink)", marginLeft: 4 }}
                    data-testid="ad-spend-status-testmode">
              ${g.spent_today_usd_including_test.toFixed(2)}
            </strong>
          </div>
        </div>
        <Pill tone={flags?.meta_ads_live_enabled ? "success" : "warn"}
              testId="ad-spend-live-flag">
          {flags?.meta_ads_live_enabled ? "LIVE ENABLED" : "SANDBOX MODE"}
        </Pill>
      </div>
      <div style={{ marginTop: 12, height: 8, borderRadius: 6, background: "var(--bo-panel-alt)", overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: dangerZone ? "#b45309" : "#c5a25f",
        }} data-testid="ad-spend-progress-bar" />
      </div>
      <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 8 }}>
        Remaining today: <strong style={{ color: "var(--bo-ink)" }}
                                data-testid="ad-spend-remaining">
          ${g.remaining_usd.toFixed(2)}
        </strong>
      </div>
      {status.per_product?.length > 0 && (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--bo-border)", paddingTop: 12 }}>
          <div className="bo-eyebrow">Per-product caps</div>
          <div style={{ display: "grid", gap: 6 }}>
            {status.per_product.map((p) => (
              <div key={p.product_id}
                   data-testid={`ad-spend-per-product-${p.product_id}`}
                   style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span>{p.product_id}</span>
                <span>
                  ${p.spent_today_usd.toFixed(2)} / ${p.cap_usd.toFixed(2)}
                  <span style={{ color: "var(--bo-muted)", marginLeft: 6 }}>
                    (${p.remaining_usd.toFixed(2)} left)
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdAccounts() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState(null);
  const [flags, setFlags] = useState(null);
  const [error, setError] = useState(null);
  const [showBind, setShowBind] = useState(false);
  const [unbindingId, setUnbindingId] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [a, s, f] = await Promise.all([
        api.get("/ads/ad-accounts"),
        api.get("/ads/spend/status"),
        api.get("/ads/feature-flags"),
      ]);
      setRows(a.data.ad_accounts || []);
      setStatus(s.data);
      setFlags(f.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleUnbind(id) {
    setUnbindingId(id);
    try {
      await api.post(`/ads/ad-accounts/${id}/unbind`);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUnbindingId(null);
    }
  }

  return (
    <Shell>
      <div className="bo-page" data-testid="ad-accounts-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">PAID ADS · SPRINT SLICE 7.1</div>
            <h1 data-testid="ad-accounts-title">Ad Accounts</h1>
            <p className="bo-page-lede">
              Bind and manage the Meta ad accounts Brand OS is authorised
              to spend inside. Every publish flows through the same
              governance rail as organic — preflight, freeze, spend cap.
            </p>
          </div>
          <div className="bo-page-actions" style={{ display: "flex", gap: 8 }}>
            <Link className="bo-btn bo-btn-ghost" to="/brand-os/ads/create"
                  data-testid="go-to-ad-wizard">
              New Ad Package →
            </Link>
            <button className="bo-btn bo-btn-primary"
                    data-testid="open-bind-ad-account"
                    onClick={() => setShowBind(true)}>
              Bind ad account
            </button>
          </div>
        </header>

        {error && <ErrorBanner error={error} testId="ad-accounts-error" />}

        <div style={{ display: "grid", gap: 16 }}>
          <SpendStatusPanel status={status} flags={flags} />

          {showBind && (
            <div className="bo-card" data-testid="bind-ad-account-modal">
              <div className="bo-eyebrow">Bind a new ad account</div>
              <div style={{ marginTop: 10 }}>
                <BindAdAccountForm
                  onCancel={() => setShowBind(false)}
                  onCreated={() => { setShowBind(false); refresh(); }}
                />
              </div>
            </div>
          )}

          {rows === null ? (
            <LoadingCard label="Loading ad accounts…" />
          ) : rows.length === 0 ? (
            <EmptyState
              testId="ad-accounts-empty"
              title="No ad accounts bound yet"
              description={
                "Bind the Meta ad account Brand OS should spend from. Every campaign built here freezes to that account with a SHA-256 audit trail."
              }
            />
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {rows.map((r) => (
                <AdAccountRow
                  key={r.id} row={r}
                  onUnbind={handleUnbind}
                  unbinding={unbindingId === r.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
